import type { VoidFunctionComponent } from 'react';
import { ControlLabel, FormGroup } from 'react-bootstrap';
import { Link } from 'react-router';
import Select from 'react-select-2';
import { userHasPermissionToCreatePolicyType, userHasPermissionToSetReleaseGroupPolicy } from '../../../../helpers/permissions';
import { Policy, PolicyType } from '../../../../types';
import { useIsQualityEnabled, useIsSecurityEnabled } from '../../../../helpers/features';

type SettingsMode =
  | { mode: 'creating' }
  | { mode: 'editing'; releaseGroupId: number };

const getOptionValue = (opt) => opt.id
const getOptionLabel = (opt) => opt.title

type PolicySelectorProps = {
  title: string;
  settingsMode: SettingsMode;
  policyType: PolicyType;
  policyOptions: Policy[];
  isLoadingPolicyOptions: boolean;
  selectedPolicy: Policy;
  onChangePolicy(option: Policy);
};

function PolicySelector({
  title,
  settingsMode,
  policyType,
  policyOptions,
  isLoadingPolicyOptions,
  selectedPolicy,
  onChangePolicy,
}: PolicySelectorProps) {
  return (
    <FormGroup>
      <ControlLabel>
        {title}
        {(settingsMode.mode === 'creating' || userHasPermissionToCreatePolicyType(policyType)) && (
          <Link to={`/policies/${policyType.toLowerCase()}`} className="margin-left-xs">(+ Create New)</Link>
        )}
      </ControlLabel>
      <Select
        placeholder="Select a policy"
        value={selectedPolicy}
        getOptionValue={getOptionValue}
        getOptionLabel={getOptionLabel}
        options={policyOptions}
        onChange={onChangePolicy}
        isDisabled={settingsMode.mode === 'editing' && !userHasPermissionToSetReleaseGroupPolicy(settingsMode.releaseGroupId, policyType)}
        isLoading={isLoadingPolicyOptions}
      />
    </FormGroup>
  )
}

type PolicySettingsProps = {
  settingsMode: SettingsMode;
  onChangeLicensingPolicy(policy: Policy);
  onChangeSecurityPolicy(policy: Policy);
  onChangeQualityPolicy(policy: Policy);
  selectedSecurityPolicy: Policy;
  selectedLicensingPolicy: Policy;
  selectedQualityPolicy: Policy;
  licensingPolicies: Policy[];
  securityPolicies: Policy[];
  qualityPolicies: Policy[];
  isLoadingPolicyOptions: boolean;
};

const PolicySettings = ({
  settingsMode,
  onChangeLicensingPolicy,
  onChangeSecurityPolicy,
  onChangeQualityPolicy,
  selectedSecurityPolicy,
  selectedLicensingPolicy,
  selectedQualityPolicy,
  licensingPolicies,
  securityPolicies,
  qualityPolicies,
  isLoadingPolicyOptions,
}): VoidFunctionComponent<PolicySettingsProps> => {
  const hasSecurity = useIsSecurityEnabled();
  const hasQuality = useIsQualityEnabled();

  return (
    <div>
      <PolicySelector
        title="License Compliance Policy"
        settingsMode={settingsMode}
        policyOptions={licensingPolicies}
        isLoadingPolicyOptions={isLoadingPolicyOptions}
        policyType="LICENSING"
        onChangePolicy={onChangeLicensingPolicy}
        selectedPolicy={selectedLicensingPolicy}
      />
      {hasSecurity && (
        <PolicySelector
          title="Vulnerability Management Policy"
          settingsMode={settingsMode}
          policyOptions={securityPolicies}
          isLoadingPolicyOptions={isLoadingPolicyOptions}
          policyType="SECURITY"
          onChangePolicy={onChangeSecurityPolicy}
          selectedPolicy={selectedSecurityPolicy}
        />
      )}
      {hasQuality && (
        <PolicySelector
          title="Quality Policy"
          settingsMode={settingsMode}
          policyOptions={qualityPolicies}
          isLoadingPolicyOptions={isLoadingPolicyOptions}
          policyType="QUALITY"
          onChangePolicy={onChangeQualityPolicy}
          selectedPolicy={selectedQualityPolicy}
        />
      )}
    </div>
  );
};

export default PolicySettings;
